import { motion } from "framer-motion";
import { ExternalLink, Flame, MousePointerClick, MessageCircle, User } from "lucide-react";
import { PlatformIcon, PLATFORM_COLORS } from "@/lib/platforms";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const VIRAL_MESSAGE_TEMPLATE = (url) =>
  `¡Hola! Necesito un favor enorme de 3 segundos. Entra a este link rápido, dale a seguir a esta cuenta y me estarás ayudando a ganar un premio en efectivo hoy: ${url} 🙏🔥`;

export default function LeaderboardCard({ profile, rank, onOutbid, onClick, hunter }) {
  const isGodMode = profile.god_mode;
  const platformColor = PLATFORM_COLORS[profile.platform_type] || "#00F0FF";

  const handleWhatsAppShare = () => {
    if (!hunter) return;
    const trackingUrl = `${BACKEND_URL}/api/v/${hunter.hunter_id}/${profile.id}`;
    const message = VIRAL_MESSAGE_TEMPLATE(trackingUrl);
    const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
    toast.success("Compartido — cada click único te da 1 viral point");
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(rank * 0.02, 0.4) }}
      data-testid={`leaderboard-card-${rank}`}
      className={
        isGodMode
          ? "god-mode-border"
          : "rounded-2xl bg-void-card border border-white/5 hover:border-neon-cyan/30 transition-colors"
      }
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">
        {/* Rank */}
        <div className="flex sm:flex-col items-center sm:items-center gap-3 sm:min-w-[64px]">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center font-heading font-black text-xl ${
              rank === 1
                ? "bg-neon-gold text-black"
                : rank <= 3
                ? "bg-neon-cyan/15 text-neon-cyan border border-neon-cyan/30"
                : "bg-white/5 text-white border border-white/10"
            }`}
          >
            {rank}
          </div>
          <div className="flex sm:hidden items-center gap-2 text-xs font-mono text-muted-foreground">
            <MousePointerClick className="w-3 h-3" /> {profile.clicks}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 flex items-center gap-3">
          {/* Photo avatar */}
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-white/10 bg-black flex items-center justify-center">
            {profile.photo_url ? (
              <img
                src={`${BACKEND_URL}${profile.photo_url}`}
                alt={profile.handle_name}
                data-testid={`profile-photo-${rank}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <User className="w-6 h-6 text-white/30" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center"
                style={{ backgroundColor: `${platformColor}22`, color: platformColor }}
                aria-hidden
              >
                <PlatformIcon platform={profile.platform_type} className="w-3.5 h-3.5" />
              </div>
              <span className="font-heading font-bold text-base sm:text-lg truncate">
                {profile.handle_name}
              </span>
              {isGodMode && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon-gold/15 text-neon-gold text-[10px] font-mono uppercase tracking-widest border border-neon-gold/30">
                  👑 God Mode
                </span>
              )}
            </div>
            <p className="text-sm text-white/80 line-clamp-1 mb-2">"{profile.custom_tagline}"</p>
            <div className="hidden sm:flex items-center gap-4 text-xs font-mono text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <MousePointerClick className="w-3 h-3" /> {profile.clicks} clicks
              </span>
              <span>{profile.platform_type}</span>
            </div>
          </div>
        </div>

        {/* Bid */}
        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-3 sm:min-w-[140px]">
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Bid
            </div>
            <div
              className={`font-mono font-bold text-xl sm:text-2xl tabular-nums ${
                isGodMode ? "text-neon-gold" : "text-neon-cyan"
              }`}
            >
              ${Number(profile.current_bid).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:min-w-[220px]">
          <a
            href={profile.profile_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            data-testid={`visit-profile-${rank}`}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-white/15 hover:border-white/40 text-sm font-mono uppercase tracking-widest transition-colors"
          >
            Visit <ExternalLink className="w-3 h-3" />
          </a>
          {hunter && (
            <button
              type="button"
              onClick={handleWhatsAppShare}
              data-testid={`whatsapp-share-${rank}`}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold text-xs transition-colors"
              style={{ boxShadow: "0 0 15px rgba(37, 211, 102, 0.4)" }}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Share
            </button>
          )}
          <Button
            data-testid={`outbid-button-${rank}`}
            onClick={onOutbid}
            className="rounded-full bg-neon-red hover:bg-neon-red/90 text-white font-bold text-sm neon-red-glow"
          >
            <Flame className="w-4 h-4 mr-1.5" /> PUJAR
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
